import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';


const SMARTLY_SCRIPT_ID = 'smartly-webchat-script';


const PersonaDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromWebDemo = location?.state?.fromWebDemo || false;
  const fromTransactionalDemo = location?.state?.fromTransactionalDemo || false;
  
  // All state hooks at the top
  const [persona, setPersona] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  
  // Balance/Recharge state
  const [balance, setBalance] = useState('');
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [rechargeAmount, setRechargeAmount] = useState('');
  const [rechargeLoading, setRechargeLoading] = useState(false);
  const [rechargeMsg, setRechargeMsg] = useState('');
  
  // PUK state
  const [puk, setPuk] = useState('');
  const [pukLoading, setPukLoading] = useState(false);
  
  // Services state
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [serviceMsg, setServiceMsg] = useState('');

  // Info state
  const [email, setEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailMsg, setEmailMsg] = useState('');

  // Fetch functions using useCallback
  const fetchBalance = useCallback(async () => {
    if (!persona?._id) return;
    setBalanceLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/persona/${persona._id}/balance`);
      setBalance(res.data.balance);
    } catch {
      setBalance('Error');
    }
    setBalanceLoading(false);
  }, [persona?._id]);

  const fetchPuk = useCallback(async () => {
    if (!persona?._id) return;
    setPukLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/persona/${persona._id}/puk`);
      setPuk(res.data.puk);
    } catch {
      setPuk('Error');
    }
    setPukLoading(false);
  }, [persona?._id]);

  const fetchServices = useCallback(async () => {
    if (!persona?._id) return;
    setServicesLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/persona/${persona._id}/services`);
      setServices(res.data);
    } catch {
      setServices([]);
    }
    setServicesLoading(false);
  }, [persona?._id]);

  // Action handlers
  const [loadingServiceIdx, setLoadingServiceIdx] = useState(null);

  const handleToggleService = useCallback(async (idx) => {
    if (!persona?._id) return;
    const service = services[idx];
    setLoadingServiceIdx(idx);
    setServiceMsg('');

    try {
      // Send request to server
      const response = await axios.patch(`${process.env.REACT_APP_API_URL}/personas/${persona._id}`, {
        services: services.map((s, index) => 
          index === idx ? { ...s, active: !s.active } : s
        )
      });
      
      // Update with server response data
      setPersona(response.data);
      setServices(response.data.services);
      setBalance(response.data.balance || '');
      setPuk(response.data.puk || '');
      setEmail(response.data.email || '');
      
      setServiceMsg(`Service ${service.name} ${!service.active ? 'enabled' : 'disabled'} successfully`);
      setTimeout(() => setServiceMsg(''), 3000);
    } catch (error) {
      console.error('Error toggling service:', error);
      setServiceMsg('Error updating service');
      setTimeout(() => setServiceMsg(''), 3000);
    }
    setLoadingServiceIdx(null);
  }, [persona?._id, services]);

  const handleRecharge = useCallback(async (e) => {
    e.preventDefault();
    if (!rechargeAmount || !persona?._id) return;
    setRechargeLoading(true);
    setRechargeMsg('');
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/persona/${persona._id}/recharge`, { amount: rechargeAmount });
      setBalance(res.data.balance);
      setRechargeMsg('Recharge successful!');
      setRechargeAmount('');
    } catch {
      setRechargeMsg('Recharge failed.');
    }
    setRechargeLoading(false);
  }, [persona?._id, rechargeAmount]);



  // Load initial data
  useEffect(() => {
    setLoading(true);
    axios.get(`${process.env.REACT_APP_API_URL}/personas/${id}`)
      .then(res => {
        setPersona(res.data);
        setBalance(res.data.balance || '');
        setPuk(res.data.puk || '');
        setEmail(res.data.email || '');
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [id]);

  // Load services when tab is selected
  useEffect(() => {
    if (tab === 2 && persona?._id) {
      fetchServices();
    }
  }, [tab, persona?._id, fetchServices]);

  // Inject Smartly webchat script
  useEffect(() => {
    if (!persona) return; // Wait for persona data

    // Clean up any existing instances
    const cleanup = () => {
      ['smartly-script', 'smartly-webchat'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
      const frame = document.querySelector('iframe[src*="smartly.ai"]');
      if (frame) frame.remove();
    };

    cleanup();

    // Prepare user data as a JS object (not string)
    const userData = {
      first_name: persona.name,
      last_name: '',
      email: persona.email || '',
      phone_number: persona.phone_number || '',
      gtu_accepted: 'true',
      comments: `Type: ${persona.type}, Balance: ${persona.balance}`
    };

    // Build the script content as a single tag
    const scriptContent = `var SMARTLY_APP_ID = "6685496f2e424416f6b1f05b";
var SMARTLY_LANG = "fr-fr";
var SMARTLY_USER_DATA = ${JSON.stringify(userData)};
(function(){d=document;s=d.createElement('script');s.src='https://cdn.smartly.ai/webchat.min.js';s.async=1;d.getElementsByTagName('head')[0].appendChild(s);})();`;

    const script = document.createElement('script');
    script.id = 'smartly-script';
    script.type = 'text/javascript';
    script.innerHTML = scriptContent;
    document.head.appendChild(script);

    return cleanup;
  }, [persona]);

  // Tab effect: fetch data when switching
  useEffect(() => {
    if (!persona?._id) return;
    if (tab === 0) fetchBalance();
    if (tab === 1 && !puk) fetchPuk();
    if (tab === 2) fetchServices();
  }, [tab, persona?._id, fetchBalance, fetchPuk, fetchServices, puk]);

  if (loading) return <div className="persona-detail-loading">Loading...</div>;
  if (!persona) return <div className="persona-detail-loading">Persona not found.</div>;

  return (
    <div className="persona-details-layout">
      {fromTransactionalDemo && (
        <div className="transactional-demo-header">
          <h2>Transactional Agent Demo</h2>
          <p>This demo showcases Ooredoo's AI-powered transactional agent with personalized experiences and API integrations.</p>
          <div className="agent-capabilities">
            <h3>Agent Capabilities</h3>
            <div className="capabilities-grid">
              <div className="capability-item">
                <span className="material-icons">api</span>
                <span>API Integrations</span>
              </div>
              <div className="capability-item">
                <span className="material-icons">sms</span>
                <span>SMS Confirmations</span>
              </div>
              <div className="capability-item">
                <span className="material-icons">account_balance</span>
                <span>Account Management</span>
              </div>
              <div className="capability-item">
                <span className="material-icons">stream</span>
                <span>Workflow Automation</span>
              </div>
            </div>
          </div>
          <p className="usage-instructions">Interact with the tabs below to explore different functionalities:</p>
        </div>
      )}


      <div className="persona-tabs">
        <button 
          className={tab === 0 ? 'active' : ''} 
          onClick={() => setTab(0)}
        >
          Balance
        </button>
        <button 
          className={tab === 1 ? 'active' : ''} 
          onClick={() => setTab(1)}
        >
          PUK
        </button>
        <button 
          className={tab === 2 ? 'active' : ''} 
          onClick={() => setTab(2)}
        >
          Services
        </button>
        <button 
          className={tab === 3 ? 'active' : ''} 
          onClick={() => setTab(3)}
        >
          Info
        </button>
      </div>

      <div className="persona-tab-content">
        {tab === 3 && (
          <div className="info-tab">
            <div className="info-grid">
              <div className="info-item">
                <label>Full Name</label>
                <div className="info-value">{persona.name}</div>
              </div>
              <div className="info-item">
                <label>Phone Number</label>
                <div className="info-value">{persona.phone_number}</div>
              </div>
              <div className="info-item">
                <label>Type</label>
                <div className="info-value">{persona.type}</div>
              </div>
              <div className="info-item">
                <label>Email</label>
                <div className="info-value editable">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter email address"
                  />
                  <button 
                    className="save-button"
                    onClick={async () => {
                      setEmailLoading(true);
                      try {
                        await axios.patch(`${process.env.REACT_APP_API_URL}/persona/${persona._id}`, { email });
                        setEmailMsg('Email updated successfully');
                        setTimeout(() => setEmailMsg(''), 3000);
                      } catch {
                        setEmailMsg('Error updating email');
                      }
                      setEmailLoading(false);
                    }}
                    disabled={emailLoading}
                  >
                    {emailLoading ? 'Saving...' : 'Save'}
                  </button>
                </div>
                {emailMsg && <div className="message">{emailMsg}</div>}
              </div>
            </div>
          </div>
        )}
        {tab === 0 && (
          <div className="balance-tab">
            <div className="balance-info">
              <h3>Current Balance</h3>
              {balanceLoading ? (
                <p>Loading...</p>
              ) : (
                <p className="balance-amount">{balance} DT</p>
              )}
            </div>
            
            <form onSubmit={handleRecharge} className="recharge-form">
              <h3>Recharge</h3>
              <div className="form-group">
                <input
                  type="number"
                  value={rechargeAmount}
                  onChange={(e) => setRechargeAmount(e.target.value)}
                  placeholder="Enter amount"
                  disabled={rechargeLoading}
                />
                <button type="submit" disabled={rechargeLoading || !rechargeAmount}>
                  {rechargeLoading ? 'Processing...' : 'Recharge'}
                </button>
              </div>
              {rechargeMsg && <p className="message">{rechargeMsg}</p>}
            </form>
          </div>
        )}

        {tab === 1 && (
          <div className="puk-tab">
            <h3>PUK Code</h3>
            {pukLoading ? (
              <p>Loading...</p>
            ) : (
              <div className="puk-display">
                <p className="puk-code">{puk || 'Click to reveal PUK'}</p>
                {!puk && (
                  <button onClick={fetchPuk} disabled={pukLoading}>
                    Reveal PUK
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {tab === 2 && (
          <div className="services-tab">
            <h3>Services</h3>
            {servicesLoading ? (
              <p>Loading...</p>
            ) : (
              <div className="services-list">
                {services.map((service, idx) => (
                  <div key={idx} className="service-item">
                    <div className="service-info">
                      <h4>{service.name}</h4>
                      <p>{service.description}</p>
                    </div>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={service.active}
                        onChange={() => handleToggleService(idx)}
                        disabled={loadingServiceIdx === idx}
                      />
                      <span className="slider" />
                    </label>
                  </div>
                ))}
                {serviceMsg && <p className="message">{serviceMsg}</p>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonaDetail;
